const encoder = new TextEncoder();
const decoder = new TextDecoder();

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const padded =
    normalized +
    '='.repeat((4 - (normalized.length % 4)) % 4);

  const binary = atob(padded);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function getSubtleCrypto(): SubtleCrypto {
  if (
    typeof crypto === 'undefined' ||
    !crypto.subtle
  ) {
    throw new Error(
      'Web Crypto API unavailable. Tobius ROOM requires a secure context (HTTPS).',
    );
  }

  return crypto.subtle;
}

export function generateRoomSecret(): string {
  if (
    typeof crypto === 'undefined' ||
    typeof crypto.getRandomValues !== 'function'
  ) {
    throw new Error(
      'Secure random generator unavailable.',
    );
  }

  const bytes = new Uint8Array(32);

  crypto.getRandomValues(bytes);

  return toBase64Url(bytes);
}

const ROOM_SECRET_PREFIX =
  'room-e2ee-secret:';

export function rememberRoomSecret(
  roomId: string,
  secret: string,
): void {
  if (
    typeof window === 'undefined' ||
    !roomId ||
    !isValidRoomSecret(secret)
  ) {
    return;
  }

  try {
    localStorage.setItem(
      `${ROOM_SECRET_PREFIX}${roomId}`,
      secret,
    );
  } catch {
    // Storage may be unavailable.
    // The URL fragment remains the source of truth.
  }
}

export function forgetRoomSecret(
  roomId: string | null | undefined,
): void {
  if (
    typeof window === 'undefined' ||
    !roomId
  ) {
    return;
  }

  try {
    localStorage.removeItem(
      `${ROOM_SECRET_PREFIX}${roomId}`,
    );
  } catch {
    // Storage may be unavailable.
  }
}

export function getRememberedRoomSecret(
  roomId: string | undefined | null,
): string | null {
  if (
    typeof window === 'undefined' ||
    !roomId
  ) {
    return null;
  }

  try {
    const key = localStorage.getItem(
      `${ROOM_SECRET_PREFIX}${roomId}`,
    );

    return key && isValidRoomSecret(key)
      ? key
      : null;
  } catch {
    return null;
  }
}

export function getRoomSecret(
  roomId?: string | null,
): string | null {
  if (
    typeof window === 'undefined'
  ) {
    return null;
  }

  const params = new URLSearchParams(
    window.location.hash.replace(/^#/, ''),
  );

  const key = params.get('k');

  if (key) {
    try {
      if (fromBase64Url(key).length === 32) {
        return key;
      }
    } catch {
      // Fall through to local cache.
    }
  }

  return getRememberedRoomSecret(roomId);
}

export function withRoomSecret(
  roomPath: string,
  secret: string,
): string {
  return `${roomPath}#k=${encodeURIComponent(secret)}`;
}

export function isValidRoomSecret(
  secret: string | null,
): secret is string {
  if (!secret) {
    return false;
  }

  try {
    return fromBase64Url(secret).length === 32;
  } catch {
    return false;
  }
}

async function getKey(
  secret: string,
): Promise<CryptoKey> {
  if (!isValidRoomSecret(secret)) {
    throw new Error(
      'Invalid ROOM secret.',
    );
  }

  const subtle = getSubtleCrypto();

  return subtle.importKey(
    'raw',
    fromBase64Url(secret),
    {
      name: 'AES-GCM',
    },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptRoomContent(
  content: string,
  secret: string,
): Promise<string> {
  if (!content) {
    throw new Error(
      'Cannot encrypt empty content.',
    );
  }

  const iv = new Uint8Array(12);

  if (
    typeof crypto === 'undefined' ||
    typeof crypto.getRandomValues !== 'function'
  ) {
    throw new Error(
      'Secure random generator unavailable.',
    );
  }

  crypto.getRandomValues(iv);

  const key = await getKey(secret);
  const subtle = getSubtleCrypto();

  const cipher = await subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    key,
    encoder.encode(content),
  );

  return JSON.stringify({
    v: 1,
    iv: toBase64Url(iv),
    ct: toBase64Url(
      new Uint8Array(cipher),
    ),
  });
}

export async function decryptRoomContent(
  payload: string,
  secret: string,
): Promise<string> {
  let parsed: {
    v?: number;
    iv?: string;
    ct?: string;
  };

  try {
    parsed = JSON.parse(payload);
  } catch {
    throw new Error(
      'Invalid encrypted payload JSON.',
    );
  }

  if (
    parsed.v !== 1 ||
    !parsed.iv ||
    !parsed.ct
  ) {
    throw new Error(
      'Invalid encrypted payload.',
    );
  }

  const iv = fromBase64Url(parsed.iv);
  const ciphertext = fromBase64Url(
    parsed.ct,
  );

  if (iv.length !== 12) {
    throw new Error(
      'Invalid AES-GCM IV.',
    );
  }

  if (ciphertext.length === 0) {
    throw new Error(
      'Empty encrypted payload.',
    );
  }

  const key = await getKey(secret);
  const subtle = getSubtleCrypto();

  const plain = await subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    key,
    ciphertext,
  );

  return decoder.decode(plain);
}
